import { z } from "zod";

export const agentBetaAnnualRevenueBuckets = [
  { label: "$0–1M", value: 0 },
  { label: "$1–5M", value: 1_000_000 },
  { label: "$5–10M", value: 5_000_000 },
  { label: "$10–50M", value: 10_000_000 },
  { label: "$50–100M", value: 50_000_000 },
  { label: "$100–500M", value: 100_000_000 },
  { label: "$500M–1B", value: 500_000_000 },
  { label: "$1B+", value: 1_000_000_000 },
] as const;

export const agentBetaAnnualRevenueValues = agentBetaAnnualRevenueBuckets.map(
  (bucket) => bucket.value,
);

export const agentBetaAnnualRevenueSchema = z.number().refine((value) => {
  return agentBetaAnnualRevenueValues.some(
    (annualRevenue) => annualRevenue === value,
  );
}, "Select an annual revenue range");

export const agentBetaProfileTypeSchema = z.enum(
  ["business", "agency", "other"],
  {
    error: "Select a team type",
  },
);

export const getAgentBetaAnnualRevenueLabel = (
  value: number | null | undefined,
): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  return (
    [...agentBetaAnnualRevenueBuckets]
      .reverse()
      .find((bucket) => value >= bucket.value)?.label ?? null
  );
};

// NOTE (Max, 2026-07-30): Extra multi-select questions shown on the beta
// waitlist form under the revenue dropdown. Each question is stored on
// User.agentBetaQuestions as `slug -> comma-separated option values`, so the
// backend column stays a flat, human-readable JSON map.
// NOTE (Yuxin, 2026-07-30): Questions ending in the `other` option let the user
// type a custom answer, persisted inline as `other:<free text>` (see
// AGENT_BETA_OTHER_ANSWER_PREFIX) so the flat map stays a single string per slug.
export const AGENT_BETA_OTHER_OPTION_VALUE = "other";
const AGENT_BETA_OTHER_ANSWER_PREFIX = `${AGENT_BETA_OTHER_OPTION_VALUE}:`;

// NOTE (Yuxin, 2026-07-30): Cap the free-text answer so a long paste (or a
// tampered client) can't bloat the stored JSON map or stretch the admin table.
// A provider name easily fits in this budget.
export const AGENT_BETA_OTHER_ANSWER_MAX_LENGTH = 80;

export const agentBetaMultiSelectQuestions = [
  {
    slug: "fulfillment-provider",
    label: "Which of these fulfillment tools do you use?",
    placeholder: "Select all that apply",
    options: [
      { value: "printful", label: "Printful" },
      { value: "supliful", label: "Supliful" },
      { value: "shipstation", label: "ShipStation" },
      { value: "shipbob", label: "ShipBob" },
      { value: "shipmonk", label: "ShipMonk" },
      { value: "shiphero", label: "ShipHero" },
      { value: "easyship", label: "Easyship" },
      { value: "deliverr", label: "Deliverr" },
      { value: "amazon-fba", label: "Amazon FBA" },
      { value: AGENT_BETA_OTHER_OPTION_VALUE, label: "Other" },
    ],
  },
  {
    slug: "ecommerce-platform",
    label: "Which ecommerce platforms do you sell on?",
    placeholder: "Select all that apply",
    options: [
      { value: "shopify", label: "Shopify" },
      { value: "printful", label: "Printful" },
      { value: "supliful", label: "Supliful" },
      { value: "woocommerce", label: "WooCommerce" },
      { value: "bigcommerce", label: "BigCommerce" },
      { value: "squarespace", label: "Squarespace" },
      { value: "wix", label: "Wix" },
      { value: "etsy", label: "Etsy" },
      { value: "amazon", label: "Amazon" },
      { value: "square", label: "Square" },
      { value: "tiktok-shop", label: "TikTok Shop" },
      { value: "instagram-shop", label: "Instagram Shop" },
      { value: AGENT_BETA_OTHER_OPTION_VALUE, label: "Other" },
    ],
  },
] as const;

// NOTE (Yuxin, 2026-07-30): Free text is stored as `other:<text>`. Commas are the
// CSV delimiter for the flat map, so we collapse them to spaces before persisting.
export const encodeAgentBetaOtherAnswer = (text: string): string => {
  const sanitized = text
    .replace(/,/g, " ")
    .trim()
    .slice(0, AGENT_BETA_OTHER_ANSWER_MAX_LENGTH)
    .trim();
  return sanitized.length > 0
    ? `${AGENT_BETA_OTHER_ANSWER_PREFIX}${sanitized}`
    : AGENT_BETA_OTHER_OPTION_VALUE;
};

export type AgentBetaQuestionSlug =
  (typeof agentBetaMultiSelectQuestions)[number]["slug"];

// NOTE (Yuxin, 2026-07-30): Waitlist form-state validation. In the form the
// answers live as `slug -> string[]` (one array per question), and every
// question is required — each must have at least one option picked. Issues are
// attached to the offending question's field path so react-hook-form surfaces
// the error inline next to that control.
export const agentBetaQuestionAnswersFormSchema = z
  .record(z.string(), z.array(z.string()))
  .superRefine((answers, ctx) => {
    for (const question of agentBetaMultiSelectQuestions) {
      if ((answers[question.slug]?.length ?? 0) === 0) {
        ctx.addIssue({
          code: "custom",
          message: "Select at least one option",
          path: [question.slug],
        });
      }
    }
  });

// NOTE (Yuxin, 2026-08-02): Extra query params from the waitlist URL (e.g.
// `?source=fulfillment`) are stored on the same `agentBetaQuestions` map as the
// answers. The prefix keeps a URL-sourced key from ever colliding with a real
// question slug, and lets the admin table render them as their own group.
export const AGENT_BETA_URL_PARAM_PREFIX = "url:";

// NOTE (Yuxin, 2026-08-02): The URL is fully attacker-controlled, so bound what
// can reach the stored JSON map: a long paste or a link stuffed with params
// shouldn't be able to bloat the column or stretch the admin table.
export const AGENT_BETA_URL_PARAM_MAX_COUNT = 20;
export const AGENT_BETA_URL_PARAM_MAX_KEY_LENGTH = 64;
export const AGENT_BETA_URL_PARAM_MAX_VALUE_LENGTH = 200;

// NOTE (Yuxin, 2026-08-02): Marketing links occasionally carry auth material
// (OAuth `code`/`state`, one-click login tokens). We capture params blindly, so
// drop anything credential-shaped rather than persisting it to the admin table.
const AGENT_BETA_URL_PARAM_SENSITIVE_PATTERNS = [
  "token",
  "password",
  "passwd",
  "secret",
  "credential",
  "signature",
  "auth",
  "session",
  "jwt",
  "apikey",
];
const AGENT_BETA_URL_PARAM_SENSITIVE_KEYS = new Set([
  "code",
  "state",
  "key",
  "sig",
  "otp",
  "pin",
]);

const normalizeUrlParamKey = (key: string): string => {
  return key.toLowerCase().replace(/[^a-z0-9]/g, "");
};

const isSensitiveUrlParamKey = (key: string): boolean => {
  const normalized = normalizeUrlParamKey(key);
  return (
    AGENT_BETA_URL_PARAM_SENSITIVE_KEYS.has(normalized) ||
    AGENT_BETA_URL_PARAM_SENSITIVE_PATTERNS.some((pattern) => {
      return normalized.includes(pattern);
    })
  );
};

const isValidUrlParamEntry = (prefixedKey: string, value: string): boolean => {
  const key = prefixedKey.slice(AGENT_BETA_URL_PARAM_PREFIX.length);
  return (
    key.length > 0 &&
    key.length <= AGENT_BETA_URL_PARAM_MAX_KEY_LENGTH &&
    value.length <= AGENT_BETA_URL_PARAM_MAX_VALUE_LENGTH &&
    !isSensitiveUrlParamKey(key)
  );
};

/**
 * Parses a `window.location.search` string into the prefixed `url:<key>`
 * entries stored on `agentBetaQuestions`. Empty and credential-shaped params are
 * dropped; the rest are trimmed and capped. Repeated keys keep the first value.
 */
export const collectAgentBetaUrlParams = (
  search: string,
): Record<string, string> => {
  const collected: Record<string, string> = {};
  for (const [key, rawValue] of new URLSearchParams(search)) {
    if (Object.keys(collected).length >= AGENT_BETA_URL_PARAM_MAX_COUNT) {
      break;
    }
    const trimmedKey = key.trim().slice(0, AGENT_BETA_URL_PARAM_MAX_KEY_LENGTH);
    const value = rawValue
      .trim()
      .slice(0, AGENT_BETA_URL_PARAM_MAX_VALUE_LENGTH);
    if (trimmedKey.length === 0 || value.length === 0) {
      continue;
    }
    if (isSensitiveUrlParamKey(trimmedKey)) {
      continue;
    }
    const prefixedKey = `${AGENT_BETA_URL_PARAM_PREFIX}${trimmedKey}`;
    if (prefixedKey in collected) {
      continue;
    }
    collected[prefixedKey] = value;
  }
  return collected;
};

/**
 * The URL-sourced entries of a stored `agentBetaQuestions` map, with the prefix
 * stripped, for rendering in the admin waitlist table.
 */
export const getAgentBetaUrlParamEntries = (
  answers: Record<string, string> | null | undefined,
): { key: string; value: string }[] => {
  if (!answers) {
    return [];
  }
  return Object.entries(answers)
    .filter(([key]) => key.startsWith(AGENT_BETA_URL_PARAM_PREFIX))
    .map(([key, value]) => ({
      key: key.slice(AGENT_BETA_URL_PARAM_PREFIX.length),
      value,
    }));
};

const agentBetaOptionValuesBySlug = new Map<string, Set<string>>(
  agentBetaMultiSelectQuestions.map((question) => [
    question.slug,
    new Set(question.options.map((option) => option.value)),
  ]),
);

const isAllowedAnswer = (slug: string, csvAnswer: string): boolean => {
  const allowedValues = agentBetaOptionValuesBySlug.get(slug);
  if (!allowedValues) {
    return false;
  }
  return csvAnswer
    .split(",")
    .filter((value) => value.length > 0)
    .every((value) => {
      // NOTE (Yuxin, 2026-07-30): A free-text "other" answer (`other:<text>`) is
      // valid only for questions that actually offer the "other" option, and the
      // typed portion must stay within the length cap so a tampered client
      // can't persist an oversized value.
      if (value.startsWith(AGENT_BETA_OTHER_ANSWER_PREFIX)) {
        const freeText = value.slice(AGENT_BETA_OTHER_ANSWER_PREFIX.length);
        return (
          allowedValues.has(AGENT_BETA_OTHER_OPTION_VALUE) &&
          freeText.length <= AGENT_BETA_OTHER_ANSWER_MAX_LENGTH
        );
      }
      return allowedValues.has(value);
    });
};

// NOTE (Max, 2026-07-30): A flat `slug -> csv` map. Kept permissive (record of
// strings) but every key must be a known question and every listed option must
// be valid, so we never persist garbage from a tampered client.
// NOTE (Yuxin, 2026-08-02): `url:`-prefixed keys are the captured URL params
// instead of answers, so they carry free text and are bounded by length caps
// rather than the option allowlist.
export const agentBetaQuestionsSchema = z
  .record(z.string(), z.string())
  .refine((answers) => {
    return Object.entries(answers).every(([key, value]) => {
      return key.startsWith(AGENT_BETA_URL_PARAM_PREFIX)
        ? isValidUrlParamEntry(key, value)
        : isAllowedAnswer(key, value);
    });
  }, "Invalid beta questionnaire answers")
  .refine((answers) => {
    const urlParamKeys = Object.keys(answers).filter((key) => {
      return key.startsWith(AGENT_BETA_URL_PARAM_PREFIX);
    });
    return urlParamKeys.length <= AGENT_BETA_URL_PARAM_MAX_COUNT;
  }, "Too many URL parameters");

export type AgentBetaQuestions = z.infer<typeof agentBetaQuestionsSchema>;

export const getAgentBetaQuestionAnswerLabels = (
  slug: AgentBetaQuestionSlug,
  csvAnswer: string | null | undefined,
): string[] => {
  if (!csvAnswer) {
    return [];
  }
  const question = agentBetaMultiSelectQuestions.find(
    (candidate) => candidate.slug === slug,
  );
  if (!question) {
    return [];
  }
  const optionLabelsByValue = new Map<string, string>(
    question.options.map((option) => [option.value, option.label]),
  );
  return csvAnswer
    .split(",")
    .filter((value) => value.length > 0)
    .map((value) => {
      if (value.startsWith(AGENT_BETA_OTHER_ANSWER_PREFIX)) {
        const freeText = value.slice(AGENT_BETA_OTHER_ANSWER_PREFIX.length);
        return freeText.length > 0 ? `Other: ${freeText}` : "Other";
      }
      return optionLabelsByValue.get(value) ?? null;
    })
    .filter((label): label is string => label !== null);
};
